import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { StatusBadge } from "./ExpertDashboard";
import { CreditCard, RefreshCw } from "lucide-react";
import { useT } from "../lib/i18n";

const eur = (cents) => `€${(cents / 100).toFixed(2)}`;

export default function MyReviews() {
  const { t } = useT();
  const [params] = useSearchParams();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/review-requests");
      setItems(r.data.review_requests);
    } catch { setItems([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const paid = params.get("paid_request");
    if (!paid) return;
    let tries = 0;
    const poll = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.get(`/review-requests/${paid}`);
        if (r.data.payment_status === "PAID") {
          clearInterval(poll);
          toast.success(t("Payment confirmed — the expert review is now in progress"));
          load();
        }
      } catch { /* keep polling */ }
      if (tries > 10) clearInterval(poll);
    }, 2000);
    return () => clearInterval(poll);
  }, [params, load, t]);

  const pay = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/review-requests/${id}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Could not start payment"));
      setBusy(false);
    }
  };

  return (
    <DashboardLayout title={t("My expert reviews")}
      action={<button onClick={load} className="text-sm font-bold text-[#475569] inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> {t("Refresh")}</button>}>
      <div className="max-w-4xl">
        {!items ? <p className="text-[#94A3B8]">{t("Loading…")}</p> : items.length === 0 ? (
          <div data-testid="reviews-empty" className="bg-white border border-dashed border-slate-200 rounded-xl p-10 text-center">
            <p className="font-display font-bold text-[#0B1B3D]">{t("No expert reviews requested yet")}</p>
            <p className="text-sm text-[#475569] mt-1">{t("Open a completed Decision Report and request a human expert review.")}</p>
            <Link to="/marketplace" className="inline-block mt-4 text-[#0EA5E9] font-bold text-sm">{t("Browse All Experts")} →</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-reviews-list">
            {items.map((rr) => (
              <div key={rr.review_request_id} data-testid={`review-row-${rr.review_request_id}`} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <p className="font-bold text-[#0B1B3D]">{rr.session_title}</p>
                  <p className="text-sm text-[#475569]">{rr.consultant_name} · {rr.turnaround}</p>
                </div>
                <p className="font-display font-black text-[#0B1B3D]">{eur(rr.customer_total_cents)}</p>
                <StatusBadge value={rr.review_status} />
                {rr.review_status === "ACCEPTED_AWAITING_PAYMENT" && (
                  <button data-testid={`pay-${rr.review_request_id}`} disabled={busy} onClick={() => pay(rr.review_request_id)}
                    className="bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-4 py-2 rounded-lg font-bold text-sm font-display inline-flex items-center gap-2 disabled:opacity-50">
                    <CreditCard className="w-4 h-4" /> {t("Complete Payment")}
                  </button>
                )}
                {rr.review_status === "REQUESTED" && <p className="text-xs text-[#94A3B8] font-mono-kz">{t("Waiting for the expert to accept")}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
